import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import { useApi } from '../hooks/useApi';
import { CompanyResult, CompanySearchResponse } from '../types/company.types';



const CompanyChecker: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<CompanyResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const api = useApi();

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get<CompanySearchResponse>(`/company/search?query=${encodeURIComponent(searchQuery)}`);
      if (response?.data) {
        setResults(response.data.items || []);
      } else {
        setResults([]);
      }
    } catch (err) {
      setError('Failed to search companies. Please try again.');
      
    } finally {
      setLoading(false);
    }
  };

  const formatAddress = (address: CompanyResult['registered_office_address']) => {
    if (!address) return 'No address available';
    
    return [
      address.address_line_1,
      address.address_line_2,
      address.locality,
      address.postal_code,
      address.country
    ].filter(Boolean).join(', ') || 'Address details not available';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Company Checker
      </Typography>

      <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
        <TextField
          fullWidth
          label="Search by Company Name or Number"
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Search'}
        </Button>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {results.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><Typography fontWeight="bold">Company Name</Typography></TableCell>
                <TableCell><Typography fontWeight="bold">Company Number</Typography></TableCell>
                <TableCell><Typography fontWeight="bold">Company Status</Typography></TableCell>
                <TableCell><Typography fontWeight="bold">Company Type</Typography></TableCell>
                <TableCell><Typography fontWeight="bold">Registered Address</Typography></TableCell>
                <TableCell><Typography fontWeight="bold">Incorporated Date</Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((company) => (
                <TableRow key={company.company_number}>
                  <TableCell>{company.company_name}</TableCell>
                  <TableCell>{company.company_number}</TableCell>
                  <TableCell>{company.company_status || 'Unknown'}</TableCell>
                  <TableCell>{company.company_type || 'Unknown'}</TableCell>
                  <TableCell>{formatAddress(company.registered_office_address)}</TableCell>
                  <TableCell>{formatDate(company.date_of_creation)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {results.length === 0 && !loading && searchQuery && (
        <Typography sx={{ textAlign: 'center', mt: 4 }}>
          No companies found matching your search.
        </Typography>
      )}
    </Box>
  );
};

export default CompanyChecker;
